import React, { useState, useEffect } from 'react';
import { createPurchaseSite, updatePurchaseSite } from '../../services/purchaseSitesService';
import { getWarehouses } from '../../services/warehousesService';

const PurchaseSiteForm = ({ isOpen, onClose, purchaseSite = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    is_active: true,
    warehouse_ids: [],
  });
  const [warehouses, setWarehouses] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    getWarehouses({ active_only: true })
      .then((res) => {
        if (res.success) setWarehouses(res.data || []);
      })
      .catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (purchaseSite) {
      setFormData({
        name: purchaseSite.name || '',
        location: purchaseSite.location || '',
        is_active: purchaseSite.is_active !== undefined ? purchaseSite.is_active : true,
        warehouse_ids: (purchaseSite.warehouses || []).map((w) => w.id),
      });
    } else {
      setFormData({
        name: '',
        location: '',
        is_active: true,
        warehouse_ids: [],
      });
    }
    setErrors({});
  }, [purchaseSite, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const toggleWarehouse = (warehouseId) => {
    setFormData((prev) => {
      const ids = prev.warehouse_ids || [];
      const next = ids.includes(warehouseId)
        ? ids.filter((id) => id !== warehouseId)
        : [...ids, warehouseId];
      return { ...prev, warehouse_ids: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre es obligatorio';
    if (!formData.warehouse_ids?.length) {
      newErrors.warehouse_ids = 'Seleccione al menos un almacén';
    }
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        location: formData.location || null,
        is_active: formData.is_active,
        warehouse_ids: formData.warehouse_ids,
      };
      if (purchaseSite) {
        await updatePurchaseSite(purchaseSite.id, payload);
      } else {
        await createPurchaseSite(payload);
      }
      onSuccess();
      onClose();
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({
          submit: error.response?.data?.message || 'Error al guardar la sede de compra',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-teal-600 px-6 py-4">
              <h3 className="text-lg font-medium text-white">
                {purchaseSite ? 'Editar sede de compra' : 'Nueva sede de compra'}
              </h3>
            </div>
            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {errors.submit && (
                <div className="bg-red-50 text-red-700 p-3 rounded text-sm border border-red-200">
                  {errors.submit}
                </div>
              )}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.name ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500'
                  }`}
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                  Ubicación
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Almacenes <span className="text-red-500">*</span>
                </label>
                <div className="border border-gray-300 rounded-md p-3 max-h-40 overflow-y-auto bg-gray-50">
                  {warehouses.length === 0 ? (
                    <p className="text-sm text-gray-500">No hay almacenes disponibles</p>
                  ) : (
                    warehouses.map((wh) => (
                      <label key={wh.id} className="flex items-center gap-2 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(formData.warehouse_ids || []).includes(wh.id)}
                          onChange={() => toggleWarehouse(wh.id)}
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                        <span className="text-sm text-gray-700">
                          {wh.name} {wh.code ? `(${wh.code})` : ''}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                {errors.warehouse_ids && (
                  <p className="mt-1 text-sm text-red-600">{errors.warehouse_ids}</p>
                )}
              </div>
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Sede activa</span>
                </label>
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-md">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50"
              >
                {loading ? 'Guardando...' : purchaseSite ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PurchaseSiteForm;
